import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { useOptionallyControlledState } from '@ipfabric/ui-components/utils/react/useOptionallyControlledState';
import type { TableFilter, TableFilterOperator } from '@ipfabric/utils';
import { type Draft, produce } from 'immer';
import { useState } from 'react';

export type RuleFilterColumn = {
  key: string;
  filter: TableFilter;
  // Optional parent column key for usage in object filter
  parent?: string;
};

export type RuleFilter = {
  type: 'ruleFilter';
  column: RuleFilterColumn | null;
  /**
   * There is a special case where rule field key contains a dynamic part.
   * At the time of writing, it was only 'device.attributes' filter, which
   * maps to 'device.attributes.\w+' in backend.
   *
   * This field holds form state of a text input for the dynamic part.
   */
  dynamicField: string;
  dynamicFieldError?: string;
  operator: TableFilterOperator | null;
  arrayOperator?: 'any' | 'all';
  value: string;
  /** related only to and shown on value. Does not cover other invalid states. */
  error?: string;
  /** indicates when an unsupported cross-column operator was detected */
  unsupportedOperatorError?: string;
  touched?: boolean;
};

export const groupFilterOperators = ['and', 'or'] as const;
export type GroupFilterOperator = (typeof groupFilterOperators)[number];
export const isGroupFilterOperator = (
  value?: string | null,
): value is GroupFilterOperator =>
  groupFilterOperators.includes(value as GroupFilterOperator);

export type GroupFilter = {
  type: 'groupFilter';
  operator: GroupFilterOperator;
  filters: (GroupFilter | RuleFilter)[];
};

export const isGroupFilter = (
  filter: GroupFilter | RuleFilter,
): filter is GroupFilter => filter.type === 'groupFilter';

export const emptyRuleFilter: RuleFilter = {
  type: 'ruleFilter',
  column: null,
  dynamicField: '',
  operator: null,
  value: '',
  unsupportedOperatorError: undefined,
};

export const emptyGroupFilter: GroupFilter = {
  type: 'groupFilter',
  operator: 'and',
  filters: [{ ...emptyRuleFilter }],
};

const updateFilterByPath = (
  filterTree: Draft<GroupFilter>,
  path: number[],
  newFilter: GroupFilter | RuleFilter,
): GroupFilter => {
  const newFilterTree = { ...filterTree };
  let foundFilter: GroupFilter | RuleFilter = filterTree;

  if (path.length === 0 && isGroupFilter(newFilter)) {
    return newFilter;
  }

  path.forEach((i, index) => {
    if (!isGroupFilter(foundFilter) || !foundFilter.filters[i]) {
      throw new IpfError('INVALID_ARGUMENT', 'Invalid advanced filters path');
    }

    if (index === path.length - 1) {
      foundFilter.filters[i] = newFilter;
    } else {
      // Fixed noUncheckedIndexedAccess NIM-19408
      foundFilter = foundFilter.filters[i]!;
    }
  });

  return newFilterTree;
};

const deleteFilterByPath = (
  filterTree: Draft<GroupFilter>,
  path: number[],
): GroupFilter => {
  const newFilterTree = filterTree;
  let foundFilter: GroupFilter | RuleFilter = filterTree;

  const itemIndex = [...path].pop();

  if (itemIndex === undefined) {
    return newFilterTree;
  }

  path.some((i, index) => {
    if (!isGroupFilter(foundFilter) || !foundFilter.filters[i]) {
      throw new IpfError('INVALID_ARGUMENT', 'Invalid advanced filters path');
    }

    if (index === path.length - 1) {
      foundFilter.filters.splice(itemIndex, 1);
      return true;
    }
    // Fixed noUncheckedIndexedAccess NIM-19408
    foundFilter = foundFilter.filters[i]!;
    return false;
  });

  return newFilterTree;
};

const touchAllRules = (filter: Draft<GroupFilter>) => {
  filter.filters = filter.filters.map((f) =>
    isGroupFilter(f) ? touchAllRules(f) : { ...f, touched: true },
  );

  return filter;
};

export interface AdvancedFilterHook {
  filter: GroupFilter;
  handleFilterChange: (
    path: number[],
    newFilter: GroupFilter | RuleFilter,
  ) => void;
  handleDeleteFilter: (path: number[]) => void;
  clipboard: GroupFilter | null;
  copy: (group: GroupFilter) => void;
  handleValidate: () => void;
}

interface Props {
  value?: GroupFilter;
  onChange?: (value: GroupFilter) => void;
  initialValue?: GroupFilter | (() => GroupFilter);
}

export const useAdvancedFilter = (props?: Props): AdvancedFilterHook => {
  const [filter, setFilter] = useOptionallyControlledState(
    props?.value,
    props?.onChange,
    props?.initialValue ?? emptyGroupFilter,
  );
  const [clipboard, setClipboard] = useState<GroupFilter | null>(null);

  const handleFilterChange = (
    path: number[],
    newFilter: GroupFilter | RuleFilter,
  ) => {
    const updatedFilter = produce({ filter }, (draft) => {
      draft.filter = updateFilterByPath(draft.filter, path, newFilter);
    });

    setFilter(updatedFilter.filter);
  };

  const handleDeleteFilter = (path: number[]) => {
    const updatedFilter = produce({ filter }, (draft) => {
      draft.filter = deleteFilterByPath(draft.filter, path);
    });

    setFilter(updatedFilter.filter);
  };

  const handleValidate = () => {
    const updatedFilter = produce({ filter }, (draft) => {
      draft.filter = touchAllRules(draft.filter);
    });

    setFilter(updatedFilter.filter);
  };

  return {
    filter,
    handleFilterChange,
    handleDeleteFilter,
    clipboard,
    copy: setClipboard,
    handleValidate,
  };
};
